import { useEffect, useState } from 'react';

/**
 * The chart palette and chrome for the Analysis view.
 *
 * Recharts needs concrete colour values (it writes `fill`/`stroke` attributes on SVG nodes), so it
 * cannot consume `hsl(var(--token))` the way the rest of the module does. That makes this file the
 * one place chart colour is decided — and the reason it is a short, deliberate list rather than a
 * palette generator.
 *
 * Why these values
 * ────────────────
 * The studio shell remaps `--primary` to a near-black charcoal (globals.css,
 * `.studio-shell.rs-theme`), which is correct for buttons and useless as a data hue. Charts
 * therefore use the app's existing chart blue — the same `#2563eb` that opens
 * `ObservabilityLayout`'s `chartPalettes.agents` and seeds `MemberAvatar` — so the two analytical
 * surfaces in the product share one colour language.
 *
 * Only TWO data colours exist here, and that is the design:
 *   - `magnitude`  — one blue, for every bar in a ranked chart and the trend area. Bar LENGTH
 *     encodes magnitude; colouring each bar darker-where-bigger would double-encode it and burn the
 *     only free channel on information already on screen.
 *   - `overage`    — red, for the BAD portion of a whole: spend past a budget, runs that failed. It
 *     means "bad", so it is a status colour and never a series colour. One red for every such
 *     portion, so a reader learns the meaning once.
 *
 * Both pairs were checked with the dataviz validator against this app's real surfaces
 * (light `#ffffff`, dark `#14171f`) and pass every gate in both modes: lightness band, chroma
 * floor, CVD separation (ΔE 29.1 light / 19.2 dark, target ≥8), normal-vision separation, and ≥3:1
 * contrast against the surface.
 */

export interface ChartTheme {
  /** Single hue for magnitude — bars and the trend area. */
  magnitude: string;
  /** Softer fill for the trend area under its stroke. */
  magnitudeFill: string;
  /** Status colour: the bad part of a whole (budget overage, failed runs). */
  overage: string;
  /** The limit / target rule. */
  reference: string;
  grid: string;
  axis: string;
  /** Wash behind the hovered bar or the crosshair band. */
  hoverBand: string;
}

const LIGHT: ChartTheme = {
  magnitude: '#2563eb',
  magnitudeFill: 'rgba(37, 99, 235, 0.14)',
  overage: '#ed2c2c',
  reference: 'rgba(15, 23, 42, 0.34)',
  grid: 'rgba(15, 23, 42, 0.08)',
  axis: 'rgba(15, 23, 42, 0.45)',
  hoverBand: 'rgba(15, 23, 42, 0.045)',
};

// Not an automatic flip of the light values — each is re-stepped for the dark surface and
// re-validated as a set, per the palette rules.
const DARK: ChartTheme = {
  magnitude: '#3987e5',
  magnitudeFill: 'rgba(57, 135, 229, 0.20)',
  overage: '#e66767',
  reference: 'rgba(226, 232, 240, 0.34)',
  grid: 'rgba(226, 232, 240, 0.09)',
  axis: 'rgba(226, 232, 240, 0.48)',
  hoverBand: 'rgba(226, 232, 240, 0.06)',
};

/**
 * Tracks the app's theme so charts repaint on a toggle.
 *
 * The app switches themes by toggling `.dark` on `<html>` (Tailwind `darkMode: ["class"]`), so a
 * `MutationObserver` on that attribute is the signal — `prefers-color-scheme` would ignore the
 * in-app toggle, which is the only way most users change it.
 */
export function useChartTheme(): ChartTheme {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark ? DARK : LIGHT;
}

/** Shared geometry. Exported so every chart in the view agrees on tick size and bar thickness. */
export const CHART_GEOMETRY = {
  /** Bar thickness. Constant by design: ranked charts grow in height, they never squash. */
  barSize: 14,
  /** Vertical space per row in a horizontal bar chart, bar + gap. */
  rowHeight: 30,
  tickFontSize: 11,
  /** Trend chart height, by available width. */
  trendHeightNarrow: 200,
  trendHeightWide: 264,
} as const;
